import React, { useRef, useState, useCallback, useEffect } from 'react';
import { ChevronsLeftRight } from 'lucide-react';

/**
 * Drag-to-reveal Before/After image slider.
 * Touch + mouse + keyboard supported. The "after" image is the base layer;
 * the "before" image sits on top inside a clipped wrapper whose width is
 * controlled by the divider position (0–100%).
 */
export default function BeforeAfterSlider({
  beforeUrl,
  afterUrl,
  beforeUrlSmall,
  afterUrlSmall,
  alt = '',
  initial = 50,
  className = '',
  rounded = true,
  testId,
}) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(initial);
  const [dragging, setDragging] = useState(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPos((x / rect.width) * 100);
  }, []);

  const onPointerDown = (e) => {
    setDragging(true);
    updateFromClientX(e.clientX);
    e.target.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    updateFromClientX(e.clientX);
  };

  const onPointerUp = (e) => {
    setDragging(false);
    e.target.releasePointerCapture?.(e.pointerId);
  };

  // Keyboard accessibility
  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') setPos((p) => Math.max(0, p - 5));
    if (e.key === 'ArrowRight') setPos((p) => Math.min(100, p + 5));
    if (e.key === 'Home') setPos(0);
    if (e.key === 'End') setPos(100);
  };

  // Auto-tease on mount: gentle wiggle so users see the slider works
  useEffect(() => {
    const t1 = setTimeout(() => setPos(initial - 12), 600);
    const t2 = setTimeout(() => setPos(initial + 12), 1100);
    const t3 = setTimeout(() => setPos(initial), 1700);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [initial]);

  return (
    <div
      ref={containerRef}
      role="slider"
      tabIndex={0}
      aria-label="Drag to compare before and after"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos)}
      data-testid={testId || 'before-after-slider'}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      className={`relative w-full select-none touch-none cursor-ew-resize overflow-hidden bg-slate-200 ${rounded ? 'rounded-2xl' : ''} ${className}`}
      style={{ aspectRatio: '4 / 3' }}
    >
      {/* AFTER image (base layer — full) */}
      <img
        src={afterUrl}
        srcSet={afterUrlSmall ? `${afterUrlSmall} 480w, ${afterUrl} 1200w` : undefined}
        sizes="(max-width: 768px) 100vw, 50vw"
        alt={`After: ${alt}`}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        onError={(e) => {
          e.currentTarget.src = 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=70&auto=format&fit=crop';
        }}
      />

      {/* AFTER label */}
      <span className="absolute top-3 right-3 z-10 bg-emerald-600 text-white text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md">
        After
      </span>

      {/* BEFORE image (clipped to position via clip-path — keeps full-size scaling) */}
      <img
        src={beforeUrl}
        srcSet={beforeUrlSmall ? `${beforeUrlSmall} 480w, ${beforeUrl} 1200w` : undefined}
        sizes="(max-width: 768px) 100vw, 50vw"
        alt={`Before: ${alt}`}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
      />

      {/* BEFORE label */}
      <span
        className="absolute top-3 z-10 bg-rose-600 text-white text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md transition-all"
        style={{ left: `clamp(12px, calc(${pos}% - 60px), calc(100% - 80px))` }}
      >
        Before
      </span>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]"
        style={{ left: `${pos}%`, transform: 'translateX(-1px)' }}
      />

      {/* Drag handle */}
      <button
        type="button"
        aria-label="Drag handle"
        tabIndex={-1}
        className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white shadow-xl ring-2 ring-emerald-500 flex items-center justify-center transition-transform ${dragging ? 'scale-110' : 'hover:scale-105'}`}
        style={{ left: `${pos}%` }}
        data-testid="ba-handle"
      >
        <ChevronsLeftRight size={20} className="text-emerald-600" />
      </button>
    </div>
  );
}
